import { action, makeObservable, observable } from 'mobx'
import { CallCenterContact } from 'call-center/models/CallCenterContact'
import {
  CallCenterResultSource,
  CallCenterSearchResult,
  CallCenterWorkspaceMemberMatch,
} from 'call-center/models/CallCenterSearchResult'
import Member from 'iam/models/Member'

function normalizeEmail(email?: string | null): string {
  return (email || '').trim().toLowerCase()
}

function resultKeyFromHubSpotContact(contact: CallCenterContact): string {
  const emailKey = normalizeEmail(contact.email)
  return emailKey ? `email:${emailKey}` : `contact:${contact.id}`
}

function resultKeyFromMember(member: Member): string {
  const emailKey = normalizeEmail(member.email)
  return emailKey ? `email:${emailKey}` : `member:${member.user.publicId}`
}

export function dedupeCallCenterMatches(
  matches: CallCenterWorkspaceMemberMatch[],
): CallCenterWorkspaceMemberMatch[] {
  const seen = new Set<string>()
  return matches.filter((match) => {
    const key = `${match.workspace.id}:${match.member.user.publicId}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

export function determineCallCenterSource(
  result: CallCenterSearchResult,
): CallCenterResultSource {
  if (result.hubspotContact && result.workspaceMembers.length > 0) {
    return 'combined'
  }

  if (result.hubspotContact) {
    return 'hubspot'
  }

  return 'worklayer'
}

export class CallCenterResultMerger {
  private resultLookup = new Map<string, CallCenterSearchResult>()

  constructor() {
    makeObservable(this)
  }

  @action
  mergeContacts(
    hubspotContacts: CallCenterContact[],
  ): CallCenterSearchResult[] {
    const newlyAdded: CallCenterSearchResult[] = []

    hubspotContacts.forEach((hubspotContact) => {
      const key = resultKeyFromHubSpotContact(hubspotContact)
      const existing = this.resultLookup.get(key)

      if (existing) {
        existing.hubspotContact = hubspotContact
        existing.source = determineCallCenterSource(existing)
        return
      }

      const result = observable({
        key,
        hubspotContact,
        workspaceMembers: [],
        source: 'hubspot',
      }) as CallCenterSearchResult

      this.resultLookup.set(key, result)
      newlyAdded.push(result)
    })

    return newlyAdded
  }

  @action
  mergeWorkspaceMembers(
    workspaceMembersMatches: CallCenterWorkspaceMemberMatch[],
  ): CallCenterSearchResult[] {
    const newlyAdded: CallCenterSearchResult[] = []

    workspaceMembersMatches.forEach((match) => {
      const key = resultKeyFromMember(match.member)
      const existing = this.resultLookup.get(key)

      if (existing) {
        existing.workspaceMembers = dedupeCallCenterMatches(
          existing.workspaceMembers.concat(match),
        )
        existing.source = determineCallCenterSource(existing)
        return
      }

      const result = observable({
        key,
        workspaceMembers: [match],
        source: 'worklayer',
      }) as CallCenterSearchResult

      this.resultLookup.set(key, result)
      newlyAdded.push(result)
    })

    return newlyAdded
  }

  getResultByKey(key: string): CallCenterSearchResult | undefined {
    return this.resultLookup.get(key)
  }

  @action
  clear() {
    this.resultLookup.clear()
  }
}
